import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { resolveTierPrice } from "@/lib/pricing";

const CartCtx = createContext(null);
const STORE_KEY = "bj_cart";

function priceFor(item) {
  let base;
  if (item.pricing_mode === "tiers" && item.price_tiers?.length) {
    const t = resolveTierPrice(item.quantity, item.price_tiers);
    base = t ?? item.unit_price;
  } else {
    base = item.unit_price;
  }
  const sides = (item.selected_sides || []).reduce((s, x) => s + (Number(x.price) || 0), 0);
  return base + sides;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORE_KEY, JSON.stringify(items));
  }, [items]);

  const add = useCallback((item) => {
    setItems((prev) => [
      ...prev,
      { ...item, key: `${item.product_id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` },
    ]);
  }, []);

  const remove = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const updateQty = useCallback((key, qty) => {
    if (qty <= 0) {
      setItems((prev) => prev.filter((i) => i.key !== key));
      return;
    }
    setItems((prev) =>
      prev.map((i) => {
        if (i.key !== key) return i;
        const next = { ...i, quantity: qty };
        if (i.pricing_mode === "tiers" && i.price_tiers?.length) {
          const t = resolveTierPrice(qty, i.price_tiers);
          if (t != null) next.unit_price = t;
        }
        return next;
      })
    );
  }, []);

  const updateSides = useCallback((key, selected_sides) => {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, selected_sides } : i)));
  }, []);

  // Clears only the active business's items (per-tenant scoped cart).
  const clear = useCallback((businessId) => {
    if (!businessId) {
      setItems([]);
      return;
    }
    setItems((prev) => prev.filter((i) => i.business_id !== businessId));
  }, []);

  // The cart is scoped per business — every selector accepts a businessId so
  // switching tenants shows a fresh cart for that tenant (items are preserved
  // separately per business).
  const itemsFor = useCallback(
    (businessId) => items.filter((i) => (i.business_id || businessId) === businessId),
    [items]
  );
  const countFor = useCallback(
    (businessId) => itemsFor(businessId).reduce((sum, i) => sum + i.quantity, 0),
    [itemsFor]
  );
  const totalFor = useCallback(
    (businessId) => itemsFor(businessId).reduce((sum, i) => sum + priceFor(i) * i.quantity, 0),
    [itemsFor]
  );

  return (
    <CartCtx.Provider value={{ items, add, remove, updateQty, updateSides, clear, itemsFor, countFor, totalFor, priceFor }}>
      {children}
    </CartCtx.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
